import { useTranslations } from "@zapier/i18n";
import { Icon } from "@zapier/zinnia-icons";
import { Link, ServiceIconShell, Text } from "@zapier/design-system";
import { RelativeTime, formatTimeSpanAbbreviatedLetter } from "@zapier/date-am";
import {
  AssetTypes,
  getBuildAssetUrl,
  getAssetIcon,
  assetTypeToSentenceCase,
} from "@zapier/utils";
import { type LinkedAsset } from "./LinkedAssetsWarning";
import styles from "./LinkedAssetRows.module.css";

interface LinkedAssetRowsProps {
  linkedAssets: LinkedAsset[];
}

function parseUpdatedAt(
  updatedAt: string | null | undefined,
): Date | undefined {
  if (!updatedAt) return undefined;
  const parsed = new Date(updatedAt);
  return Number.isFinite(parsed.getTime()) ? parsed : undefined;
}

/**
 * Bordered connection rows with a larger icon, name, and an "Updated ..."
 * timestamp when the asset has one. Used only inside the dependencies
 * accordion; every other caller keeps the plain `LinkedAssetsList` so this
 * design never leaks into their rendering.
 */
export function LinkedAssetRows({ linkedAssets }: LinkedAssetRowsProps) {
  const t = useTranslations("linked-assets-warning");
  const tFallback = useTranslations("fallbacks");

  return (
    <ul className={styles.list}>
      {linkedAssets.map((asset) => {
        const assetTitle =
          asset.title ||
          (asset.type === AssetTypes.TableSubscription
            ? t("messages.locked-zap")
            : tFallback("untitled-asset", {
                assetType: assetTypeToSentenceCase(asset.type),
              }));
        const assetUrl = getBuildAssetUrl(asset.type, asset.id);
        const updatedAt = parseUpdatedAt(asset.updatedAt);
        const icon = (
          <Icon color="BrandOrange" name={getAssetIcon(asset.type)} size={14} />
        );

        const info = (
          <div className={styles.info}>
            <Text type="Body2">{assetTitle}</Text>
            {updatedAt && (
              <Text type="MinimalPrint2" color="TextWeakest" tag="div">
                {t.rich("messages.updated-ago", {
                  time: () => (
                    <RelativeTime
                      date={updatedAt}
                      formatter={formatTimeSpanAbbreviatedLetter}
                    />
                  ),
                })}
              </Text>
            )}
          </div>
        );

        return (
          <li key={`${asset.type}:${asset.id}`} className={styles.item}>
            {assetUrl ? (
              <Link
                href={assetUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.link}
              >
                <ServiceIconShell size="small" hasBorder>
                  {icon}
                </ServiceIconShell>
                {info}
              </Link>
            ) : (
              <div className={styles.link}>
                <ServiceIconShell size="small" hasBorder>
                  {icon}
                </ServiceIconShell>
                {info}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
